import { createContext, useContext, useState, useEffect } from 'react'
import { CURRENCIES, CURRENCY } from '../utils/constants'
import { useSettings } from './SettingsContext'

const CurrencyContext = createContext()

// The currency that existing data is assumed to be in if not specified
const DATA_CURRENCY = 'USD'

export const useCurrency = () => {
  const context = useContext(CurrencyContext)
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider')
  }
  return context
}

export const CurrencyProvider = ({ children }) => {
  const { settings } = useSettings()
  
  // Use system settings as the source of truth
  const baseCurrency = settings.currency || CURRENCY.DEFAULT
  const rates = settings.currency_rates || { [CURRENCY.DEFAULT]: 1 }
  
  // Allow user override, but default to system setting
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    const saved = localStorage.getItem('selectedCurrency')
    // If saved currency is valid, use it, otherwise use system default
    return saved && CURRENCIES[saved] ? saved : (settings.currency || CURRENCY.DEFAULT)
  })

  // Update selected currency if system default changes (optional, but requested behavior implies global sync)
  useEffect(() => {
    if (settings.currency && CURRENCIES[settings.currency]) {
      // We update the selected currency to match system default to ensure "Global" application
      // But we still allow user to change it temporarily via UI if needed (which updates localStorage)
      // For now, let's respect the system setting as the primary driver per user request
      setSelectedCurrency(settings.currency)
      localStorage.setItem('selectedCurrency', settings.currency)
    }
  }, [settings.currency])

  const getCurrencyInfo = (currencyCode = selectedCurrency) => {
    return CURRENCIES[currencyCode] || CURRENCIES[CURRENCY.DEFAULT]
  }

  const convertAmount = (amount, fromCurrency, toCurrency) => {
    if (amount === null || amount === undefined || isNaN(amount)) return 0
    
    // Default fromCurrency to DATA_CURRENCY if not specified (assuming DB data is USD)
    const sourceCurrency = fromCurrency || DATA_CURRENCY
    const targetCurrency = toCurrency || selectedCurrency

    const fromRate = sourceCurrency === baseCurrency ? 1 : (rates[sourceCurrency] || 1)
    const toRate = targetCurrency === baseCurrency ? 1 : (rates[targetCurrency] || 1)
    
    // Convert to base currency first, then to target currency
    // Formula: Amount / Rate(Source) * Rate(Target)
    // Rate is "1 Base = X Currency"
    const amountInBase = parseFloat(amount) / fromRate
    return amountInBase * toRate
  }

  const formatCurrency = (amount, currencyCode = selectedCurrency, options = {}) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return getCurrencyInfo(currencyCode).symbol + '0.00'
    }

    const { fromCurrency } = options
    const currency = getCurrencyInfo(currencyCode)
    
    // Default to DATA_CURRENCY if not specified
    const sourceCurrency = fromCurrency || DATA_CURRENCY
    
    let numericAmount = parseFloat(amount)
    
    // Convert if necessary
    if (sourceCurrency !== currencyCode) {
       numericAmount = convertAmount(numericAmount, sourceCurrency, currencyCode)
    }

    const defaultOptions = {
      style: 'currency',
      currency: currency.code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      ...options
    }

    try {
      return new Intl.NumberFormat(currency.locale, defaultOptions).format(numericAmount)
    } catch (error) {
      // Fallback formatting
      const formattedNumber = numericAmount.toFixed(2)
      return currency.position === 'before' 
        ? `${currency.symbol}${formattedNumber}`
        : `${formattedNumber}${currency.symbol}`
    }
  }

  const formatCurrencyCompact = (amount, currencyCode = selectedCurrency, options = {}) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return getCurrencyInfo(currencyCode).symbol + '0'
    }

    const { fromCurrency } = options
    const currency = getCurrencyInfo(currencyCode)
    
    const sourceCurrency = fromCurrency || DATA_CURRENCY
    let numericAmount = parseFloat(amount)
    
    if (sourceCurrency !== currencyCode) {
        numericAmount = convertAmount(numericAmount, sourceCurrency, currencyCode)
    }

    try {
      return new Intl.NumberFormat(currency.locale, {
        style: 'currency',
        currency: currency.code,
        notation: 'compact',
        maximumFractionDigits: 1
      }).format(numericAmount)
    } catch (error) {
      // Fallback
      let value = numericAmount
      let suffix = ''
      
      if (value >= 1000000) {
        value = value / 1000000
        suffix = 'M'
      } else if (value >= 1000) {
        value = value / 1000
        suffix = 'K'
      }
      
      const formattedValue = value.toFixed(value >= 100 ? 0 : 1)
      return currency.position === 'before' 
        ? `${currency.symbol}${formattedValue}${suffix}`
        : `${formattedValue}${suffix}${currency.symbol}`
    }
  }

  const parseCurrencyInput = (input) => {
    if (!input) return 0
    
    // Remove currency symbols and formatting
    const cleaned = input.toString()
      .replace(/[^\d.-]/g, '')
      .replace(/^-+/, '-')
      .replace(/-(?!^)/g, '')
    
    const parsed = parseFloat(cleaned)
    return isNaN(parsed) ? 0 : parsed
  }

  const changeCurrency = (currencyCode) => {
    if (CURRENCIES[currencyCode]) {
      setSelectedCurrency(currencyCode)
    }
  }
  
  // Legacy support alias
  const refreshCurrencyConfig = async () => {
      // No-op, handled by SettingsContext
      return Promise.resolve()
  }

  const value = {
    selectedCurrency,
    baseCurrency,
    rates,
    currencyInfo: getCurrencyInfo(),
    currencies: CURRENCIES,
    formatCurrency,
    formatCurrencyCompact,
    convertAmount,
    parseCurrencyInput,
    changeCurrency,
    getCurrencyInfo,
    refreshCurrencyConfig
  }

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  )
}
